import React, { useState, useContext, useEffect } from "react";
import { MdExpandMore, MdExpandLess, MdArrowDownward, MdArrowUpward } from "react-icons/md";
import { Typography } from "@mui/material";

import styles from "./CorrectlyOrderQuestion.module.css";
import StyledButton from "../components/StyledButton";
import AlignCenter from "../components/AlignCenter";
import { LangContext } from "../contexts/langContext";
import { RenderTemplateContext } from "../contexts/renderTemplateContext";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import { WebsocketContext } from "../contexts/websocketContext";
import { CorrectlyOrderRenderProps, TranslationData } from "@common";

interface RenderProps extends CorrectlyOrderRenderProps {
  readOnly?: boolean;
  showCorrect?: boolean;
}

interface Option {
  text: TranslationData;
  id: number;
}

const shuffle = <Type,>(unshuffled: Array<Type>): Array<Type> => {
  const shuffled: Array<Type> = unshuffled;
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = temp;
  }
  return shuffled;
};

const CorrectlyOrderQuestion: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  best,
  worst,
  option1,
  option2,
  option3,
  option4,
  correctIdOrder,
  readOnly = false,
  showCorrect = false,
}) => {
  const { overrideQuestionSlideAnswerDesc } = useContext(RenderTemplateContext);
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const { translatePageText, translateTranslationData } = useContext(LangContext);
  const { isMultiplayerHost } = useContext(WebsocketContext);
  const displayCorrectAnswers = showCorrect;

  // Shuffle array but always keep one element 'wrong'
  const [options, setOptions] = useState<Array<Option>>([
    { text: option3, id: 3 },
    ...shuffle<Option>([
      { text: option1, id: 1 },
      { text: option2, id: 2 },
      { text: option4, id: 4 },
    ]),
  ]);

  useEffect(() => {
    if (displayCorrectAnswers === true) {
      const correctOrderArray = correctIdOrder.split(",").filter((el: string) => Number(el));
      const correctOrder: Array<Option> = [];
      [...options].forEach((option: Option) => {
        correctOrderArray.forEach((id, index) => {
          if (option.id + "" === id) {
            correctOrder[index] = option;
          }
        });
      });
      setOptions(correctOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCorrectAnswers]);

  function checkAnswer() {
    const correctOrderArray = correctIdOrder.split(",").filter((el: string) => Number(el));
    const answerOrderArray = options.map((option: Option) => option.id);

    const isCorrect = correctOrderArray + "" === answerOrderArray + "";
    let correctOrderText = "";
    if (isCorrect === false) {
      correctOrderText = "<b>" + translatePageText("test.correctOrder") + "</b><br/> ";
      correctOrderArray.forEach((id, index) => {
        options.forEach((option: Option) => {
          if (option.id === Number(id)) {
            correctOrderText += index + 1 + ". " + translateTranslationData(option.text) + "<br/>";
          }
        });
      });
    }

    const answerStats = {
      val1: 0,
      val2: isCorrect ? 1 : 0,
      val3: isCorrect ? 0 : 1,
    };
    onSelectAnswer(isCorrect ? "correct" : "incorrect", answerStats);
    overrideQuestionSlideAnswerDesc(correctOrderText);
  }

  const handleArrowPressed = (currentIndex: any, moveUp: any) => {
    const indexDiff = moveUp ? -1 : 1;
    const items = Array.from(options);
    const [reorderedItem] = items.splice(currentIndex + indexDiff, 1);
    items.splice(currentIndex, 0, reorderedItem);

    setOptions(items);
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        fontFamily: "arial",
      }}
    >
      <AlignCenter withMaxWidth marginTop="5vh">
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "stretch",
              width: "100%",
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                flex: "100% auto",
                paddingRight: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography
                  style={{
                    fontSize: "14px",
                    margin: "16px 0 0 0",
                    opacity: 0.6,
                  }}
                >
                  {translateTranslationData(best)}
                </Typography>
                <MdExpandLess style={{ fontSize: "1.8em" }} />
                <div
                  style={{
                    height: "100%",
                    margin: "-0.76em 0",
                    width: 2,
                    background: "white",
                  }}
                />
                <MdExpandMore style={{ fontSize: "1.8em" }} />
                <Typography
                  style={{
                    fontSize: "14px",
                    margin: "0 0 16px 0",
                    opacity: 0.6,
                  }}
                >
                  {translateTranslationData(worst)}
                </Typography>
              </div>
            </div>
            <ul className={styles.items} style={{ width: "90%", transition: "all 0.2s ease" }}>
              {options.map((option: Option, index: number) => (
                <li
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  key={index}
                >
                  <div
                    style={{
                      border: "solid 2px #d0d0d0",
                      borderRadius: "0.3em",
                      padding: "0.3em",
                      width: "80%",
                    }}
                  >
                    <Typography variant={isMultiplayerHost ? "caption" : "body1"}>
                      {translateTranslationData(option.text)}
                    </Typography>
                  </div>
                  <button
                    style={{
                      padding: 0,
                      margin: "0 6px 0 8px",
                      background: "none",
                      border: "none",
                      cursor: index === 0 ? "auto" : readOnly ? "auto" : "pointer",
                      color: "white",
                      opacity: index === 0 ? 0.5 : readOnly ? 0.5 : 1,
                    }}
                    onClick={
                      index === 0
                        ? undefined
                        : !readOnly
                        ? () => handleArrowPressed(index, true)
                        : undefined
                    }
                  >
                    <MdArrowUpward style={{ marginTop: 2, fontSize: 24 }} />
                  </button>
                  <button
                    style={{
                      padding: 0,
                      marginRight: 6,
                      background: "none",
                      border: "none",
                      cursor: index === options.length - 1 ? "auto" : readOnly ? "auto" : "pointer",
                      color: "white",
                      opacity: index === options.length - 1 ? 0.5 : readOnly ? 0.5 : 1,
                    }}
                    onClick={
                      index === options.length - 1
                        ? undefined
                        : !readOnly
                        ? () => handleArrowPressed(index, false)
                        : undefined
                    }
                  >
                    <MdArrowDownward style={{ marginTop: 2, fontSize: 24 }} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </AlignCenter>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        <StyledButton
          hasCooldown
          onClick={checkAnswer}
          style={{ paddingRight: 42, paddingLeft: 42 }}
          label={translatePageText("general.done")}
          disabled={readOnly || false}
        />
      </div>
    </div>
  );
};

export default CorrectlyOrderQuestion;
